import { motion } from "framer-motion";
import { Send, Handshake, CalendarClock, BadgeCheck } from "lucide-react";
import { useLang } from "@/i18n";

const reveal = {
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] },
};

const steps = [
  { number: "01", icon: Send, title: "s1t", text: "s1x" },
  { number: "02", icon: Handshake, title: "s2t", text: "s2x" },
  { number: "03", icon: CalendarClock, title: "s3t", text: "s3x" },
  { number: "04", icon: BadgeCheck, title: "s4t", text: "s4x" },
];

export default function Process() {
  const { t } = useLang();

  return (
    <section id="process" className="py-28 lg:py-40">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-16">
        <div className="lg:col-span-5">
          <div className="lg:sticky lg:top-36">
            <motion.div {...reveal}>
              <p className="overline-label" data-testid="process-overline">{t("p_over")}</p>
              <h2 className="mt-6 font-serif font-semibold text-4xl sm:text-5xl tracking-tight leading-tight" data-testid="process-heading">
                {t("p_h1a")} <span className="text-brand-300">{t("p_h1b")}</span>
              </h2>
              <p className="mt-6 text-base text-cream/55 leading-relaxed max-w-md" data-testid="process-subcopy">
                {t("p_sub")}
              </p>
              <a
                href="#contact"
                data-testid="process-start-btn"
                className="mt-10 inline-block bg-brand text-white text-xs uppercase tracking-[0.2em] font-bold px-9 py-5 hover:bg-brand-600 transition-colors duration-300"
              >
                {t("p_cta")}
              </a>
            </motion.div>
          </div>
        </div>

        <div className="lg:col-span-7">
          {steps.map(({ number, icon: Icon, title, text }, i) => (
            <motion.div
              key={number}
              {...reveal}
              transition={{ ...reveal.transition, delay: i * 0.08 }}
              className="relative border-t border-brand/15 py-12 first:border-t-0 first:pt-0 group"
              data-testid={`process-step-${i + 1}`}
            >
              <div className="flex items-start gap-8">
                <span
                  className="font-serif text-7xl sm:text-8xl font-light text-brand/15 leading-none select-none transition-colors duration-500 group-hover:text-brand/30"
                  aria-hidden="true"
                >
                  {number}
                </span>
                <div>
                  <span className="inline-flex items-center justify-center w-11 h-11 border border-brand/30 text-brand mb-5">
                    <Icon size={20} />
                  </span>
                  <h3 className="font-serif font-semibold text-2xl sm:text-3xl text-cream">{t(title)}</h3>
                  <p className="mt-4 text-sm sm:text-base text-cream/55 leading-relaxed max-w-xl">{t(text)}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
